import React, { useEffect, useRef, useState } from 'react';
import { MdArrowDownward } from 'react-icons/md';
import {
  useChatHintContext,
  useChatHintPreference,
} from '../../hooks/useChatHint';
import { useChatSocket } from '../../services/chatSocket';
import { MBox, MButton, MIcon, MSpinner } from '../Monetize';
import { ChatWelcome } from './ChatWelcome';
import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';
import { useChatScroll } from './useChatScroll';

export const ChatContainer: React.FC = () => {
  const {
    messages,
    isLoading,
    isThinking,
    isReconnecting,
    isRestoringSession,
    sendMessage,
  } = useChatSocket();

  const { suggestions } = useChatHintContext();
  const { hintsEnabled, enableHints } = useChatHintPreference();

  const messageInputRef = useRef<HTMLDivElement>(null);
  const [inputHeight, setInputHeight] = useState(0);

  const { scrollRef, contentRef, scrollToEnd, isContentBelow } = useChatScroll({
    isTurnRunning: isLoading,
  });

  const userMessages = messages.filter(({ role }) => role === 'user');
  const hasUserMessages = userMessages.length > 0;
  const previousMessages = userMessages.map(({ content }) => content);
  const showWelcome = !hasUserMessages && !isLoading && !isThinking;

  // The composer isn't rendered while a session restores, so measure again
  // once it is.
  useEffect(() => {
    const inputElement = messageInputRef.current;
    if (!inputElement) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setInputHeight(inputElement.getBoundingClientRect().height);
    });
    observer.observe(inputElement);
    setInputHeight(inputElement.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [isRestoringSession]);

  if (isRestoringSession) {
    // Nothing conversation-related renders here, including MessageInput --
    // messages may still hold synchronous default/stale content during
    // this window, and showing it (or a usable-looking input that silently
    // no-ops on send) would be exactly the jump/dead-end this state exists
    // to avoid. Placed after all hooks above so it never skips one.
    return (
      <MBox h="100%" display="flex" alignItems="center" justifyContent="center">
        <MSpinner size="lg" />
      </MBox>
    );
  }

  return (
    <MBox
      h="100%"
      display="flex"
      flexDirection="column"
      bg="bg.canvas"
      position="relative"
      overflow="hidden"
    >
      <MBox
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="200px"
        pointerEvents="none"
        bgGradient="linear-gradient(180deg, rgba(140, 50, 255, 0.04) 0%, rgba(70, 89, 218, 0.02) 50%, transparent 100%)"
        zIndex={0}
      />

      <MBox
        flex={1}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        ref={scrollRef}
        position="relative"
        zIndex={1}
        css={{
          scrollBehavior: 'auto',
          overscrollBehavior: 'contain',
          // Lets the latest turn size itself to the panel (`cqh`).
          containerType: 'size',
        }}
      >
        <MBox ref={contentRef} display="flex" flexDirection="column">
          {showWelcome ? (
            <ChatWelcome
              onSuggestionClick={sendMessage}
              suggestions={suggestions}
              hintsEnabled={hintsEnabled}
              onEnableHints={enableHints}
            />
          ) : (
            // `aria-busy` holds announcements while an answer streams in, so
            // a screen reader reads the finished reply once instead of every
            // token; the header status announces progress in the meantime.
            <MBox
              role="log"
              aria-label="Conversation with Monty"
              aria-live="polite"
              aria-relevant="additions"
              aria-busy={isLoading}
              display="flex"
              flexDirection="column"
              gap={4}
              px={4}
              py={5}
              width="100%"
              maxW="1440px"
              mx="auto"
            >
              <MessageList
                messages={messages}
                isLoading={isLoading}
                isReconnecting={isReconnecting}
              />
            </MBox>
          )}
        </MBox>
      </MBox>

      {isContentBelow && !showWelcome && (
        <MBox
          position="absolute"
          bottom={`${inputHeight + 12}px`}
          left="50%"
          transform="translateX(-50%)"
          zIndex={10}
        >
          <MButton
            variant="secondary"
            size="sm"
            onClick={scrollToEnd}
            borderRadius="full"
            boxShadow="md"
          >
            <MIcon as={MdArrowDownward} mr={1} />
            Jump to latest
          </MButton>
        </MBox>
      )}

      <MBox ref={messageInputRef} position="relative" zIndex={2}>
        <MessageInput
          onSendMessage={sendMessage}
          previousMessages={previousMessages}
          isLoading={isLoading}
        />
      </MBox>
    </MBox>
  );
};
